import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PATH } from '@constants';
import { contextNamesSelector } from '@selectors';
import { useSelector, useTenantRole } from '@hooks';
import { useProject } from '@hooks/cluster';

import { ChaseSpinner } from '@components';
import { Layout } from '@components/Layouts';

import { withSuspense } from './utils';

const ProjectSettingsPage = lazy(
  () => import('@pages/ProjectSettings/ProjectSettingsPage'),
);
const ProjectGeneralSettingsPage = lazy(
  () => import('@pages/ProjectSettings/ProjectGeneralSettingsOutlet'),
);
const ProjectUsersSettingsPage = lazy(
  () => import('@pages/ProjectSettings/ProjectUsersSettingsOutlet'),
);
const ProjectSettingsReportsPage = lazy(
  () => import('@pages/ProjectSettings/ProjectSettingsReportsOutlet'),
);

export default function ProjectSettingsRoutes() {
  const { organizationName } = useSelector(contextNamesSelector);

  const { loading, defaultRole, isDefault } = useProject();
  const { isProjectManager, isInitialized } = useTenantRole();

  const defaultPath = isProjectManager
    ? PATH.PROJECT_SETTINGS
    : PATH.PROJECT_SETTINGS_USERS;

  if (!isInitialized) {
    return (
      <Layout title="Project Settings">
        <Layout.Content>
          <div className="mt-24 flex justify-center">
            <ChaseSpinner color="black" className="h-14 w-14" />
          </div>
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route
        element={withSuspense(ProjectSettingsPage, {
          isManager: isProjectManager,
          loading,
        })}
      >
        {isProjectManager && (
          <>
            <Route
              index
              element={withSuspense(ProjectGeneralSettingsPage, {
                isDefault,
                defaultRole,
              })}
            />
            <Route
              path="general"
              element={withSuspense(ProjectGeneralSettingsPage, {
                isDefault,
                defaultRole,
              })}
            />
            {organizationName && (
              <Route
                path="reports"
                element={withSuspense(ProjectSettingsReportsPage)}
              />
            )}
          </>
        )}

        <Route
          path="users"
          element={withSuspense(ProjectUsersSettingsPage, {
            isManager: isProjectManager,
          })}
        />

        <Route path="*" element={<Navigate replace to={defaultPath} />} />
      </Route>
    </Routes>
  );
}
